import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { createRandom } from '@/lib/motion'
import { SPARKLE_PATH } from '@/components/landing/decor/sparkle'

interface NightSkyProps {
    className?: string
}

const STARS = (() => {
    const rand = createRandom(4812)

    return Array.from({ length: 90 }, () => ({
        x: Math.round(rand() * 1600),
        y: Math.round(rand() * 760),
        r: Math.round((0.6 + rand() * 1.6) * 10) / 10,
        o: Math.round((0.3 + rand() * 0.6) * 100) / 100
    }))
})()

const CONSTELLATION = [[220, 180], [330, 120], [420, 196], [540, 150], [610, 236]]
const CONSTELLATION_B = [[1080, 140], [1180, 210], [1300, 160], [1420, 230]]

const SPARKS = [
    { x: 330, y: 120, size: 18 },
    { x: 1300, y: 160, size: 16 },
    { x: 860, y: 90, size: 12 },
    { x: 120, y: 420, size: 10 },
    { x: 1500, y: 470, size: 12 }
]

// CONTACT backdrop drawn as geometry: a deep summer night with a field of points, two dotted
// constellations, ✦ marks, a soft band of light and horizon arcs.
const NightSky: FC<NightSkyProps> = ({ className }): ReactNode => {
    const id = `ns${useId().replace(/[^a-zA-Z0-9]/g, '')}`

    return (
        <svg viewBox='0 0 1600 1000' preserveAspectRatio='xMidYMin slice' aria-hidden='true' focusable='false' className={className}>
            <defs>
                <linearGradient id={`${id}-bg`} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0%' stopColor='#2b3659' />
                    <stop offset='60%' stopColor='#1b2338' />
                    <stop offset='100%' stopColor='#141b2e' />
                </linearGradient>
                <linearGradient id={`${id}-band`} x1='0' y1='0' x2='1' y2='1'>
                    <stop offset='0%' stopColor='#fbfaf6' stopOpacity='0' />
                    <stop offset='50%' stopColor='#fbfaf6' stopOpacity='0.09' />
                    <stop offset='100%' stopColor='#fbfaf6' stopOpacity='0' />
                </linearGradient>
            </defs>

            <rect width='1600' height='1000' fill={`url(#${id}-bg)`} />
            <path d='M0 520L620 0H1060L280 1000H0Z' fill={`url(#${id}-band)`} />

            {STARS.map((star, index) => (
                <circle key={index} cx={star.x} cy={star.y} r={star.r} fill='#fbfaf6' opacity={star.o} />
            ))}

            <g fill='none' stroke='#fbfaf6' strokeOpacity='0.28' strokeDasharray='2 6'>
                <path d={`M${CONSTELLATION.map(([x, y]) => `${x} ${y}`).join('L')}`} />
                <path d={`M${CONSTELLATION_B.map(([x, y]) => `${x} ${y}`).join('L')}`} />
            </g>
            {[...CONSTELLATION, ...CONSTELLATION_B].map(([x, y]) => (
                <circle key={`c${x}-${y}`} cx={x} cy={y} r='2.4' fill='#fbfaf6' />
            ))}

            {SPARKS.map(spark => (
                <path
                    key={`${spark.x}-${spark.y}`}
                    d={SPARKLE_PATH}
                    fill='#f3d27a'
                    opacity='0.85'
                    transform={`translate(${spark.x - spark.size / 2} ${spark.y - spark.size / 2}) scale(${Math.round((spark.size / 24) * 100) / 100})`}
                />
            ))}

            <g fill='none' stroke='#fbfaf6'>
                <path d='M-100 900A900 260 0 0 1 1700 900' strokeOpacity='0.12' />
                <path d='M-100 960A900 240 0 0 1 1700 960' strokeOpacity='0.08' strokeDasharray='3 9' />
            </g>
        </svg>
    )
}

export default NightSky
